import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import useFleetStore from '../../stores/useFleetStore';
import usePartsStore from '../../stores/usePartsStore';
import { useApi } from '../../apiHelpers';
import { useToast } from '../../components/Toast/ToastContext';
import PageLayout from '../../components/ui/PageLayout';
import { buttonClass } from '../../components/ui/Button';
import { Select } from '../../components/ui/Select';
import { Field } from '../../components/ui/Field';
import { Dialog } from '../../components/ui/Dialog';
import { Alert } from '../../components/ui/Alert';
import Reveal from '../../components/ui/Reveal';
import { Panel } from '../../components/ui/Panel';
import { LogoSpinner } from '../../components/ui/LogoSpinner';

// Icons
const DroneIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
        <path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0M2.04 4.326c.325 1.329 2.532 2.54 3.717 3.19.48.263.793.434.743.484-.08.08-.162.158-.242.234-.416.396-.787.749-.758 1.266.035.634.618.824 1.214 1.017.577.188 1.168.38 1.286.983.082.417-.075.988-.22 1.52-.215.782-.406 1.48.22 1.48 1.5-.5 3.798-3.186 4-5 .138-1.243-2-2-3.5-2.5-.478-.16-.755.081-.99.284-.172.15-.322.279-.51.216-.445-.148-2.5-2-1.5-2.5.78-.39.952-.171 1.227.182.078.099.163.208.273.318.609.304.662-.132.723-.633.039-.322.081-.671.277-.867.434-.434 1.265-.791 2.028-1.12.712-.306 1.365-.587 1.579-.88A7 7 0 1 1 2.04 4.327Z"/>
    </svg>
);

const CheckIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
        <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0"/>
    </svg>
);

const GENERATIONS = ['F020', 'F021', 'D020', 'D021'].map((g) => ({ key: g, label: g }));

const LABEL = 'block text-xs font-medium text-on-surface-variant mb-1.5';

const PartSelect = ({ label, value, onChange, options, placeholder = "Select..." }) => (
    <div>
        <span className={LABEL}>{label}</span>
        <Select
            label={label}
            placeholder={placeholder}
            value={value == null || value === '' ? null : String(value)}
            onChange={onChange}
            options={options.map((opt) => ({ key: String(opt.ffpartnum), label: opt.title }))}
        />
    </div>
);

const AddBatch = ({ history }) => {
    const api = useApi();
    const toast = useToast();
    const parts = usePartsStore((state) => state.parts);
    const refreshFleet = useFleetStore((state) => state.refreshFleet);

    // State management
    const [qtyOfNewDrones, setQtyOfNewDrones] = useState('1');
    const [configs, setConfigs] = useState([]);
    const [serialGen, setSerialGen] = useState('F020');
    const [configChoice, setConfigChoice] = useState(null);
    const [serialDofm, setSerialDofm] = useState('');
    const [serialBatch, setSerialBatch] = useState('');
    const [openSerialNumbers, setOpenSerialNumbers] = useState([]);
    const [manuDeets, setManuDeets] = useState([]);
    const [batchDetails, setBatchDetails] = useState([]);
    const [frameid, setFrameid] = useState('');
    const [gpsid, setGpsid] = useState('');
    const [escid, setEscid] = useState('');
    const [fcid, setFcid] = useState('');
    const [motorid, setMotorid] = useState('');
    const [rclinkprim, setRclinkprim] = useState('');
    const [rclinksec, setRclinksec] = useState('');
    const [propellerid, setPropellerid] = useState('');
    const [firmwareid, setFirmwareid] = useState('');
    const [ledid, setLedid] = useState('');
    const [notes, setNotes] = useState('');
    const [showModal, setShowModal] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Fetch configurations, manufacture details, and batch details on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        api.get(`/configurations`, {
            onSuccess: (data) => setConfigs(data),
            errorAlert: 'Configs could not be found, please try again'
        });

        api.get(`/manufacturedetails`, {
            onSuccess: (data) => {
                // Sort by dofmreadable descending (newest first: 2509 > 2508 > 2501)
                const sorted = [...data].sort((a, b) =>
                    b.dofmreadable.localeCompare(a.dofmreadable)
                );
                setManuDeets(sorted);
                // Auto-select the latest DofM
                if (sorted.length > 0) {
                    setSerialDofm(sorted[0].dofmserial);
                }
            },
            errorAlert: 'Manufacture details could not be found, please try again'
        });

        api.get(`/batchdetails`, {
            onSuccess: (data) => {
                // Sort by batchserial descending (newest first). batchserial is
                // hex (Phase 1c: 0006, 000A, …) so parse base 16 — base 10 would
                // read '000A' as 0 and collapse all hex batches together.
                const sorted = [...data].sort((a, b) =>
                    parseInt(b.batchserial, 16) - parseInt(a.batchserial, 16)
                );
                setBatchDetails(sorted);
                // Auto-select the latest batch
                if (sorted.length > 0) {
                    setSerialBatch(sorted[0].batchserial.toString().padStart(4, '0'));
                }
            },
            errorAlert: 'Batch details could not be found, please try again'
        });
    }, []);

    // Handler for modal submission – uses the new GET /multi endpoint.
    const handleModalSubmit = (e) => {
        e?.preventDefault();
        api.get(`/opensn/multi/${serialGen}/${serialDofm}/${serialBatch}`, {
            onSuccess: (serials) => {
                const ordered = serials.sort(
                    (a, b) => parseInt(a.dronereadable, 10) - parseInt(b.dronereadable, 10)
                );
                setOpenSerialNumbers(ordered);
                setShowModal(false);
            },
            errorAlert: 'Serials could not be found, please try again'
        });
    };

    // Log new drones to console (preview mode) and display preview
    const logNewDrones = (e) => {
        e.preventDefault();
        const totalNeeded = parseInt(qtyOfNewDrones, 10);
        if (openSerialNumbers.length < totalNeeded) {
            toast.warning('Not enough serial numbers!');
            return;
        }
        // Log the preview of serial numbers that will be used
        const previewSerials = openSerialNumbers.slice(0, totalNeeded).map(sn => sn.serialnumber);
        console.log('Serial Numbers Preview:', previewSerials);

        const uuidAuto = dayjs(new Date()).format('YYMMDDHHMMssSSS');
        for (let i = 0; i < totalNeeded; i++) {
            const newDrone = {
                listkey: 0,
                uuid: `${uuidAuto}${i}`,
                condition: 'NYC',
                notes,
                hostport: 0,
                serialnumber: openSerialNumbers[i].serialnumber,
                frameid,
                gpsid,
                escid,
                fcid,
                motorid,
                rclinkprim,
                rclinksec,
                propellerid,
                firmwareid,
                ledid,
            };
            console.log('New Drone (Log Only):', newDrone);
            // For logging, mark the serial as assigned locally.
            openSerialNumbers[i].assigned = true;
            console.log('Updated Serial Info:', openSerialNumbers[i]);
        }
    };

    // Handle form submission – sends the drones array to /fleet/batch,
    // then calls the POST /multi endpoint to mark the used serials as assigned.
    const handleSubmit = async (e) => {
        e.preventDefault();
        const totalNeeded = parseInt(qtyOfNewDrones, 10);
        if (openSerialNumbers.length < totalNeeded) {
            toast.warning('Not enough serial numbers!');
            return;
        }

        setIsSubmitting(true);

        const uuidAuto = dayjs(new Date()).format('YYMMDDHHMM');
        const newDrones = [];
        const usedSerialNumbers = [];
        for (let i = 0; i < totalNeeded; i++) {
            const serial = openSerialNumbers[i];
            usedSerialNumbers.push(serial.serialnumber);
            newDrones.push({
                title: 0,
                listkey: 0,
                uuid: `${uuidAuto}${i}`,
                condition: 'NYC',
                notes,
                hostport: 0,
                serialnumber: serial.serialnumber,
                frameid,
                gpsid,
                escid,
                fcid,
                motorid,
                rclinkprim,
                rclinksec,
                propellerid,
                firmwareid,
                ledid,
                generation: inferGeneration(serialGen)
            });
        }

        try {
            await api.post(`/fleet`, {
                body: newDrones
            });

            await api.post(`/opensn/multi/${serialGen}/${serialDofm}/${serialBatch}`, {
                body: { serialNumbers: usedSerialNumbers }
            });

            refreshFleet();
            history.push('/fleet');
        } catch (error) {
            toast.error("Can't add drones, please try again");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Cancel handler
    const handleCancel = () => {
        history.goBack();
    };

    // Helper function to infer generation from model serial
    const inferGeneration = (modelSerial) => {
        if (modelSerial === 'F021' || modelSerial === 'D021') return '2.1';
        return '2';
    };

    // When configuration is picked, update parts fields accordingly.
    const handleConfigUpdate = (index) => {
        const conf = configs[index];
        if (!conf) return;
        setFrameid(conf.frameid);
        setGpsid(conf.gpsid);
        setEscid(conf.escid);
        setFcid(conf.fcid);
        setMotorid(conf.motorid);
        setRclinkprim(conf.rclinkprim);
        setRclinksec(conf.rclinksec);
        setPropellerid(conf.propellerid);
        setFirmwareid(conf.firmwareid);
        setLedid(conf.ledid);
    };

    // Use parts store for parts filtering.
    const filteredFrames = parts.filter((part) =>
        part.parttype.replace(/\s+/g, '').toLowerCase().includes('frame')
    );
    const filteredESC = parts.filter((part) =>
        part.parttype.replace(/\s+/g, '').toLowerCase().includes('esc')
    );
    const filteredFC = parts.filter((part) =>
        part.parttype.replace(/\s+/g, '').toLowerCase().includes('flightcontroller')
    );
    const filteredGPS = parts.filter((part) =>
        part.parttype.replace(/\s+/g, '').toLowerCase().includes('gps')
    );
    const filteredMotor = parts.filter((part) =>
        part.parttype.replace(/\s+/g, '').toLowerCase().includes('motor')
    );
    const filteredRC = parts.filter((part) =>
        part.parttype.replace(/\s+/g, '').toLowerCase().includes('rclink')
    );
    const filteredProp = parts.filter((part) =>
        part.parttype.replace(/\s+/g, '').toLowerCase().includes('propeller')
    );
    const filteredFirmware = parts.filter((part) =>
        part.parttype.replace(/\s+/g, '').toLowerCase().includes('firmware')
    );
    const filteredLED = parts.filter((part) =>
        part.parttype.replace(/\s+/g, '').toLowerCase().includes('led')
    );

    // If the modal is still open, render it.
    if (showModal) {
        return (
            <Dialog
                open
                onClose={() => {}}
                dismissible={false}
                hideClose
                size="md"
                title="Select Serial Info"
                meta="Choose generation, date, and batch"
                onSubmit={handleModalSubmit}
                footer={(
                    <>
                        <Link to="/fleet" className={buttonClass('quiet', 'md')}>
                            Cancel
                        </Link>
                        <button
                            type="submit"
                            disabled={!serialDofm || !serialBatch}
                            className={buttonClass('filled', 'md')}
                        >
                            {!serialDofm || !serialBatch ? 'Loading...' : 'Continue'}
                        </button>
                    </>
                )}
            >
                <div className="grid grid-cols-3 gap-3 mb-4">
                    <div>
                        <label className="block text-xs font-medium text-on-surface-variant mb-1.5">Generation</label>
                        <Select
                            label="Generation"
                            value={serialGen}
                            onChange={setSerialGen}
                            options={GENERATIONS}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-on-surface-variant mb-1.5">Date of Mfg</label>
                        <Select
                            label="Date of Mfg"
                            value={serialDofm || null}
                            onChange={setSerialDofm}
                            options={manuDeets.map((manu) => {
                                const readable = manu.dofmreadable;
                                const year = readable.slice(0, 2);
                                const month = readable.slice(2, 4);
                                const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
                                const monthName = monthNames[parseInt(month, 10) - 1] || month;
                                return { key: String(manu.dofmserial), label: `${monthName}'${year}` };
                            })}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-on-surface-variant mb-1.5">Batch</label>
                        <Select
                            label="Batch"
                            value={serialBatch || null}
                            onChange={setSerialBatch}
                            options={batchDetails.map((batch) => {
                                const batchNum = batch.batchserial.toString().padStart(4, '0');
                                return { key: batchNum, label: `#${parseInt(batch.batchserial, 16)}` };
                            })}
                        />
                    </div>
                </div>

                <Alert tone="warning" variant="note">
                    Only use this if you know what you're doing. This will reserve serial numbers.
                </Alert>
            </Dialog>
        );
    }

    // Main form render
    return (
        <PageLayout kind="focus" className="pt-4">
            {/* Header */}
            <div className="mb-6">
                <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded bg-primary/20 flex items-center justify-center text-primary-ink">
                            <DroneIcon />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-white">Add Drones</h1>
                            <p className="text-on-surface-variant text-sm">
                                {serialGen} • Batch #{parseInt(serialBatch, 10)}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={() => setShowModal(true)}
                        className={buttonClass('tonal', 'md')}
                    >
                        Change Batch
                    </button>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                {/* Quantity & Configuration */}
                <Panel label="Quantity and configuration">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-medium text-on-surface-variant mb-1.5">Quantity</label>
                            <Field
                                type="number"
                                min="1"
                                max={openSerialNumbers.length || 999}
                                value={qtyOfNewDrones}
                                onChange={(e) => setQtyOfNewDrones(e.target.value)}
                            />
                            <p className="text-xs text-on-surface-variant mt-1">
                                {openSerialNumbers.length} available
                            </p>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-on-surface-variant mb-1.5">Configuration</label>
                            <Select
                                label="Configuration"
                                placeholder="Select preset..."
                                value={configChoice}
                                onChange={(key) => { setConfigChoice(key); handleConfigUpdate(Number(key) - 1); }}
                                options={configs.map((cfg) => ({ key: String(cfg.id), label: cfg.title }))}
                            />
                        </div>
                    </div>
                </Panel>

                {/* Divider */}
                <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-outline-variant" />
                    <span className="text-sm text-on-surface-variant">or select parts manually</span>
                    <div className="flex-1 h-px bg-outline-variant" />
                </div>

                {/* Parts Selection Grid */}
                <Panel title="Components">
                    <div className="grid grid-cols-2 gap-3">
                        <PartSelect
                            label="Frame"
                            value={frameid}
                            onChange={setFrameid}
                            options={filteredFrames}
                        />
                        <PartSelect
                            label="Flight Controller"
                            value={fcid}
                            onChange={setFcid}
                            options={filteredFC}
                        />
                        <PartSelect
                            label="Firmware"
                            value={firmwareid}
                            onChange={setFirmwareid}
                            options={filteredFirmware}
                        />
                        <PartSelect
                            label="ESCs"
                            value={escid}
                            onChange={setEscid}
                            options={filteredESC}
                        />
                        <PartSelect
                            label="GPS"
                            value={gpsid}
                            onChange={setGpsid}
                            options={filteredGPS}
                        />
                        <PartSelect
                            label="Motors"
                            value={motorid}
                            onChange={setMotorid}
                            options={filteredMotor}
                        />
                        <PartSelect
                            label="RC Primary"
                            value={rclinkprim}
                            onChange={setRclinkprim}
                            options={filteredRC}
                        />
                        <PartSelect
                            label="RC Secondary"
                            value={rclinksec}
                            onChange={setRclinksec}
                            options={filteredRC}
                        />
                        <PartSelect
                            label="Propellers"
                            value={propellerid}
                            onChange={setPropellerid}
                            options={filteredProp}
                        />
                        <PartSelect
                            label="LED Panel"
                            value={ledid}
                            onChange={setLedid}
                            options={filteredLED}
                        />
                    </div>
                </Panel>

                {/* Notes */}
                <Panel label="Notes">
                    <label className="block text-xs font-medium text-on-surface-variant mb-1.5">Notes (optional)</label>
                    <Field
                        type="text"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Add any notes about this batch..."
                    />
                </Panel>

                {/* Serial Numbers Preview */}
                <div className="!mt-0">
                <Reveal show={openSerialNumbers.length > 0 && parseInt(qtyOfNewDrones, 10) > 0}>
                <div className="pt-4">
                    <Panel title={`Drones to be added (${Math.min(parseInt(qtyOfNewDrones, 10), openSerialNumbers.length)})`}>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-40 overflow-y-auto">
                            {openSerialNumbers
                                .slice(0, parseInt(qtyOfNewDrones, 10))
                                .map((sn, index) => (
                                    <Panel key={index} on="panel" padding="sm" label={`Drone ${sn.dronereadable}`}>
                                        <div className="flex items-center gap-2 text-sm">
                                            <span className="text-primary font-bold">{sn.dronereadable}</span>
                                            <span className="text-on-surface-variant font-mono text-xs truncate">
                                                {sn.serialnumber.slice(-8)}
                                            </span>
                                        </div>
                                    </Panel>
                                ))}
                        </div>
                    </Panel>
                </div>
                </Reveal>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-2">
                    <button
                        type="button"
                        className={buttonClass('tonal', 'md')}
                        onClick={handleCancel}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        disabled={!frameid}
                        className={buttonClass('tonal', 'md')}
                        onClick={logNewDrones}
                    >
                        Log Preview
                    </button>
                    <button
                        type="submit"
                        disabled={!frameid || isSubmitting}
                        aria-busy={isSubmitting || undefined}
                        className={`${buttonClass('filled', 'md')} flex-1`}
                    >
                        {isSubmitting ? (
                            <span className="flex items-center justify-center gap-2">
                                <LogoSpinner size="inline" />
                                Adding...
                            </span>
                        ) : (
                            <span className="flex items-center justify-center gap-2">
                                <CheckIcon />
                                Add {qtyOfNewDrones} Drone{parseInt(qtyOfNewDrones, 10) !== 1 ? 's' : ''}
                            </span>
                        )}
                    </button>
                </div>
            </form>
        </PageLayout>
    );
};

export default AddBatch;
